import React, { useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTimezone } from '@app/contexts/useTimezone';
import { TIMEZONE_OPTIONS } from '@app/contexts/timezoneOptions';
import { useNavigate } from 'react-router-dom';

const TimezoneDisplay: React.FC = () => {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<{
    left: number;
    top: number;
    transform: string;
  } | null>(null);
  const timezoneLabel = useMemo(() => {
    const option = TIMEZONE_OPTIONS.find((tz) => tz.value === timezone);
    return option ? option.label : timezone;
  }, [timezone]);

  const updateTooltipPosition = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const margin = 8; // spacing below the chip
    const tooltipMaxWidth = 280; // keep in sync with class max width

    // Decide alignment based on available space to avoid clipping
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    let left = rect.left + rect.width / 2;
    let transform = 'translate(-50%, 0)';

    if (spaceRight < tooltipMaxWidth / 2 && spaceLeft >= tooltipMaxWidth / 2) {
      // Align left edge of tooltip with chip's left edge
      left = rect.left;
      transform = 'translate(0, 0)';
    } else if (spaceLeft < tooltipMaxWidth / 2 && spaceRight >= tooltipMaxWidth / 2) {
      // Align right edge of tooltip with chip's right edge
      left = rect.right;
      transform = 'translate(-100%, 0)';
    }

    setTooltipStyle({
      left,
      top: rect.bottom + margin,
      transform,
    });
  }, []);

  return (
    <div className="flex items-center gap-2">
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
        Timezone
      </label>
      <div
        ref={containerRef}
        className="px-3 py-1 text-xs font-medium bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md border border-gray-200 dark:border-gray-600 whitespace-nowrap relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/20"
        onMouseEnter={() => {
          updateTooltipPosition();
          setIsTooltipVisible(true);
        }}
        onMouseLeave={() => setIsTooltipVisible(false)}
        onMouseMove={updateTooltipPosition}
        onClick={() => navigate('/account?tab=preferences#timezone')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/account?tab=preferences#timezone');
          }
        }}
        title="Change timezone in Account → Preferences"
      >
        {timezoneLabel}
        {isTooltipVisible && tooltipStyle
          ? createPortal(
              <div
                className="fixed z-[9999] pointer-events-none"
                style={{
                  left: tooltipStyle.left,
                  top: tooltipStyle.top,
                  transform: tooltipStyle.transform,
                }}
              >
                <div className="px-3 py-2 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-200 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg whitespace-normal break-words max-w-[280px]">
                  Click to change your timezone in Account Settings → Preferences.
                </div>
              </div>,
              document.body,
            )
          : null}
      </div>
    </div>
  );
};

export default TimezoneDisplay;
